import React from 'react';

/**
 * The type-specific facts a shopper needs before they buy.
 *
 * The admin collects a full panel of these per product type — allergens and
 * dietary flags for food, billing cycle and trial for subscriptions, licence
 * and platform for digital goods — and until now none of it reached a shopper.
 * Components for exactly this existed (FoodDetails, DigitalDetails, FoodCard,
 * SubscriptionCard…) but every one of them hung off `pages/Products.tsx` and
 * `pages/ProductDetails.tsx`, which are not routed; the live pages are
 * ProductsPage and ProductDetailPage. So the merchant ticked "contains
 * peanuts", the value was stored, and the person eating it never saw it.
 *
 * Renders nothing when the product carries no attributes, which is the case for
 * every product created before the panels were filled in.
 */

type Attributes = Record<string, any> | null | undefined;

export interface ProductTypeSpecsProps {
  product: any;
  className?: string;
  /** Heading colour token. Layouts on a card surface pass --card-text. */
  inkVar?: string;
}

const parseAttributes = (raw: Attributes): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return raw as Record<string, any>;
};

const titleCase = (value: string) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/** Billing cycle stored as 'monthly' / 'month' / 'MONTHLY' → "month". */
const CYCLE_NOUN: Record<string, string> = {
  daily: 'day',
  day: 'day',
  weekly: 'week',
  week: 'week',
  biweekly: '2 weeks',
  monthly: 'month',
  month: 'month',
  quarterly: 'quarter',
  quarter: 'quarter',
  biannual: '6 months',
  semiannual: '6 months',
  yearly: 'year',
  annual: 'year',
  annually: 'year',
  year: 'year',
};

/**
 * "/month" for a subscription, so the price on the page is not mistaken for a
 * one-off charge. Returns null for everything else.
 */
export function subscriptionInterval(product: any): string | null {
  const type = String(product?.productType || '').toLowerCase();
  if (type !== 'subscription') return null;
  const cycle = String(parseAttributes(product?.attributes)?.subscription?.billingCycle || '')
    .toLowerCase()
    .trim();
  if (!cycle) return null;
  return CYCLE_NOUN[cycle] || cycle;
}

export type SpecRow = { label: string; value: React.ReactNode };

export interface ProductSpecs {
  rows: SpecRow[];
  allergens: string[];
  bundleDescription: string;
}

/**
 * What this product's type has to say, as data.
 *
 * Split out from the component because customer-frontend has no DOM-rendering
 * test setup — react-dom resolves to the hoisted v19 while this app is on v18 —
 * and every existing test here is a logic test. Keeping the decisions in a
 * plain function means they stay covered.
 */
export function productSpecs(product: any): ProductSpecs {
  const type = String(product?.productType || '').toLowerCase();
  const attributes = parseAttributes(product?.attributes);
  return {
    rows: buildRows(type, attributes),
    allergens:
      (type === 'food' || type === 'ala_carte') && Array.isArray(attributes?.food?.allergens)
        ? attributes.food.allergens.filter(Boolean).map(String)
        : [],
    bundleDescription:
      type === 'bundle' ? String(attributes?.bundle?.bundleDescription || '') : '',
  };
}

export { titleCase };

/**
 * The "/month" that turns a price into a subscription price.
 *
 * Renders nothing for every other type, so it is safe to drop next to any
 * price. Sized in `em` so it scales with whatever type the card sets.
 */
export const BillingInterval: React.FC<{ product: any }> = ({ product }) => {
  const interval = subscriptionInterval(product);
  if (!interval) return null;
  return (
    <span style={{ fontSize: '0.5em', fontWeight: 600, opacity: 0.75, marginLeft: 2 }}>
      /{interval}
    </span>
  );
};

type Row = SpecRow;

function buildRows(type: string, a: Record<string, any>): Row[] {
  const rows: Row[] = [];
  const push = (label: string, value: any) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    if (Array.isArray(value) && value.length === 0) return;
    rows.push({ label, value: Array.isArray(value) ? value.map(titleCase).join(', ') : value });
  };

  if (type === 'food' || type === 'ala_carte') {
    const f = a.food || {};
    push('Serving size', f.servingSize);
    push('Spice level', f.spicyLevel ? titleCase(f.spicyLevel) : null);
    push('Served', f.temperature ? titleCase(f.temperature) : null);
    push('Preparation time', f.preparationTime ? `${f.preparationTime} min` : null);
    push('Dietary', f.dietaryInfo);
  }

  if (type === 'digital') {
    const d = a.digital || {};
    push('Format', d.fileType);
    push('File size', d.fileSize);
    push('Access', d.accessType ? titleCase(d.accessType) : null);
    push('Licence', d.licenseType ? titleCase(d.licenseType) : null);
    push('Works on', d.platforms);
    push('Account required', d.requiresAccount ? 'Yes' : null);
    push('Link valid for', d.expiryDays ? `${d.expiryDays} days` : null);
  }

  if (type === 'subscription') {
    const s = a.subscription || {};
    const noun = CYCLE_NOUN[String(s.billingCycle || '').toLowerCase()] || s.billingCycle;
    push('Billed', noun ? `Every ${noun}` : null);
    push('Free trial', s.trialDays ? `${s.trialDays} days` : null);
    push('Renews', s.autoRenew === false ? 'Manually' : s.billingCycle ? 'Automatically' : null);
    push('Cancellation', s.cancellationPolicy);
  }

  if (type === 'membership') {
    const m = a.membership || {};
    push('Tier', m.membershipLevel);
    push('Valid for', m.duration ? `${m.duration} days` : null);
    push('Renews', m.autoRenew ? 'Automatically' : null);
    push('Transferable', m.transferable ? 'Yes' : null);
  }

  if (type === 'service') {
    const s = a.service || {};
    push('Type', s.serviceType);
    push('Duration', s.duration ? `${s.duration} min` : null);
    push('Booking', s.requiresBooking ? 'Required' : null);
    push('Available', s.availableDays);
    push('Free cancellation', s.cancellationHours ? `Up to ${s.cancellationHours}h before` : null);
  }

  if (type === 'event_package') {
    const c = a.catering || {};
    const min = c.minPax ?? c.paxConfig?.minPax;
    const max = c.maxPax ?? c.paxConfig?.maxPax;
    push('Guests', min && max ? `${min}–${max}` : min || max || null);
    push('Notice required', c.advanceNoticeDays ? `${c.advanceNoticeDays} days` : null);
    push('Venue needed', c.requiresVenue ? 'Yes' : null);
  }

  if (type === 'bundle') {
    const b = a.bundle || {};
    push('Includes', Array.isArray(b.includedProducts) && b.includedProducts.length
      ? `${b.includedProducts.length} product${b.includedProducts.length === 1 ? '' : 's'}`
      : null);
    push('Minimum quantity', b.minQuantity && b.minQuantity > 1 ? b.minQuantity : null);
    push('Offer ends', b.validUntil ? new Date(b.validUntil).toLocaleDateString() : null);
  }

  if (type === 'normal' || type === 'physical_goods') {
    const p = a.physical || {};
    push('Brand', p.manufacturer);
    push('Warranty', p.warranty);
  }

  return rows;
}

export const ProductTypeSpecs: React.FC<ProductTypeSpecsProps> = ({
  product,
  className = '',
  inkVar = 'var(--card-text, var(--color-text, #111827))',
}) => {
  // Allergens are a legal disclosure in most markets, so they get their own
  // block rather than a row in a spec list a shopper can skim past.
  const { rows, allergens, bundleDescription } = productSpecs(product);

  if (rows.length === 0 && allergens.length === 0 && !bundleDescription) return null;

  const muted = `color-mix(in srgb, ${inkVar} 65%, transparent)`;

  return (
    <div className={className}>
      {bundleDescription && (
        <p className="text-sm mb-4" style={{ color: muted }}>
          {bundleDescription}
        </p>
      )}

      {rows.length > 0 && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {rows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-4">
              <dt className="text-sm shrink-0" style={{ color: muted }}>
                {row.label}
              </dt>
              <dd
                className="text-sm font-medium text-right"
                style={{ color: inkVar }}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {allergens.length > 0 && (
        <div
          className="mt-5 rounded-xl px-4 py-3"
          style={{
            // Tinted from the platform warning token so it reads as a caution
            // on every tenant palette, light or dark, without a literal hex.
            backgroundColor: 'color-mix(in srgb, var(--color-warning, #b45309) 12%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-warning, #b45309) 35%, transparent)',
          }}
        >
          <p className="text-sm font-semibold" style={{ color: inkVar }}>
            Allergen information
          </p>
          <p className="text-sm mt-1" style={{ color: inkVar }}>
            Contains {allergens.map(titleCase).join(', ')}.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductTypeSpecs;
